import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import EmailIcon from "@mui/icons-material/Email";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import { Avatar, Box, Button, Card, CardContent, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { isNil } from "lodash";
import type { FC } from "react";
import { useAgentWithId } from "../../admin/agents/useAgentWithId";
import type { UserRole } from "../../auth/auth";
import { useCustomerWithId } from "../../customers/useCustomerWithId";
import type { Currency } from "../../data/BaseEntity";
import { getOrderPrice } from "../../orders/csvExport/getOrderPrice";
import OrderStatusView2 from "../../orders/orderStatus/OrderStatusView2";
import { StatusColors } from "../../orders/orderStatus/StatusColors";
import type { SavedOrderWithNames } from "../../orders/orderTypes";
import { OrderProducts } from "./OrderProducts";

export const moneyText = (value: number | null, currency: Currency): string =>
  isNil(value) ? `n.d.` : `${value} ${currency}`;

const Balance: FC<{ order: SavedOrderWithNames; paid: number }> = ({ order, paid }) => {
  const toBePaid = getOrderPrice(order);
  const value = toBePaid.subtract(paid).toDisplayValue(order.currency);
  return (
    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
      Saldo: {value}
    </Typography>
  );
};

const Paid: FC<{ paid: number; currency: Currency }> = ({ paid, currency }) => (
  <Typography variant="body2" color="text.secondary">
    {" "}
    Wpłacono: {paid} {currency}
  </Typography>
);

const OrderName: FC<{ name: string; id?: number; onClick: () => void }> = ({ name, id, onClick }) => {
  const value = id ? `${id} · ${name}` : name;
  return (
    <Typography
      variant="h6"
      sx={{
        fontSize: "1.1rem",
        mb: 1,
        cursor: "pointer",
        transition: "color 0.2s ease",
        "&:hover": { color: "primary.main" },
        fontWeight: "bold",
      }}
      onClick={onClick}
    >
      {value}
    </Typography>
  );
};

interface Props {
  readonly value: SavedOrderWithNames;
  readonly showHistorical: boolean;
  readonly role: UserRole | undefined;
  readonly onClick: () => void;
  readonly onContextMenu: () => void;
  readonly viewMode: "grid" | "list";
}

export const OrderCard: FC<Props> = ({ value, onClick, viewMode }) => {
  const { customer } = useCustomerWithId(value.customerId);
  const { data: agent } = useAgentWithId(value.supervisorId ?? customer?.accountManager?.id);

  return (
    <Box sx={viewMode === "grid" ? { mb: 3, breakInside: "avoid", width: "100%" } : { width: "100%" }}>
      <Card
        sx={{
          width: "100%",
          borderLeft: "8px solid",
          borderLeftColor: `${StatusColors[value.status]}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Stack direction="row" sx={{ mb: 2, justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <OrderName name={value.name} id={value.userFriendlyId} onClick={onClick} />
              <Stack direction="row" spacing={2} sx={{ mb: 1, alignItems: "center" }}>
                <OrderStatusView2 value={value.status} size="small" />
                <Stack direction="row" spacing={0.5} color="text.secondary" sx={{ alignItems: "center" }}>
                  <Tooltip title="Liczba osób płatnych">
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                      <GroupIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: "500" }}>
                        {value.participantsNumber?.numberOfPaid}
                      </Typography>
                    </Stack>
                  </Tooltip>
                  {value.participantsNumber?.numberOfStaff && (
                    <Tooltip title="Liczba osób bezpłatnych (np. opiekunowie, kierowcy)">
                      <Stack direction="row" spacing={0.5} sx={{ color: "success.main", alignItems: "center" }}>
                        <CardGiftcardIcon fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: "500" }}>
                          {value.participantsNumber.numberOfStaff}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
              <Paid paid={value.paymentSum} currency={value.currency} />
              <Balance order={value} paid={value.paymentSum} />
              {value.paymentSum == 0 && value.advance && value.advance.amount && value.advance.deadline && (
                <Box
                  sx={{
                    mt: 0.5,
                    bgcolor: "rgba(237, 108, 2, 0.1)",
                    color: "warning.main",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    textAlign: "right",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ lineHeight: 1.2, fontWeight: "bold", display: "block" }}>
                    Zaliczka: {moneyText(value.advance.amount, value.currency)}
                  </Typography>
                  <Typography variant="caption" sx={{ lineHeight: 1.2, display: "block" }}>
                    do {value.advance.deadline}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
          <Grid container spacing={4} sx={{ mt: 1, mb: 3, justify: "space-between" }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Box sx={{ pl: 1 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Produkty
                </Typography>
                <OrderProducts values={value.products} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card variant="outlined" sx={{ bgcolor: "rgba(0,0,0,0.02)", borderStyle: "dashed" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: "0.5px" }}
                  >
                    Opiekun zamówienia
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.light" }}>
                        <PersonIcon fontSize="small" sx={{ color: "white" }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: "500" }}>
                        {agent?.name}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                      <Avatar
                        sx={{ width: 32, height: 32, bgcolor: "transparent", color: "text.secondary", flexShrink: 0 }}
                      >
                        <EmailIcon fontSize="small" />
                      </Avatar>
                      <Typography
                        variant="body2"
                        component="a"
                        href={`mailto:${agent?.contact.przezmorzeEmail}`}
                        sx={{
                          wordBreak: "break-all",
                          textDecoration: "none",
                          color: "text.primary",
                          borderBottom: "1px dashed rgba(0,0,0,0.3)",
                          transition: "all 0.2s ease",
                          "&:hover": { color: "primary.main", borderColor: "primary.main", opacity: 0.8 },
                        }}
                      >
                        {agent?.contact.przezmorzeEmail}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: "transparent", color: "text.secondary" }}>
                        <PhoneIcon fontSize="small" />
                      </Avatar>
                      <Typography
                        variant="body2"
                        component="a"
                        href={`tel:${agent?.contact.phone?.replace(/\s+/g, "")}`}
                        sx={{
                          textDecoration: "none",
                          color: "text.primary",
                          borderBottom: "1px dashed rgba(0,0,0,0.3)",
                          transition: "all 0.2s ease",
                          "&:hover": { color: "primary.main", borderColor: "primary.main", opacity: 0.8 },
                        }}
                      >
                        {agent?.contact.phone}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Stack direction="row" sx={{ mt: "auto", pt: 2, justifyContent: "flex-end" }}>
            <Button variant="outlined" color="inherit" sx={{ borderColor: "#ddd" }} onClick={onClick}>
              Szczegóły
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
